import type { FormEvent } from 'react';
import { Suspense, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { experimentsDefaultDisableReshuffling } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import type { CreateExperimentModalSource } from '@gonfalon/experiments/analytics';
import {
  resolveCreateExperimentModalSource,
  trackCreateExperimentModalClosed,
  trackCreateExperimentModalSubmitted,
  trackCreateExperimentModalViewed,
} from '@gonfalon/experiments/analytics';
import { getLatestFlagVersionId } from '@gonfalon/experiments/getLatestFlagVersionId';
import { getUniqueExperimentKey } from '@gonfalon/experiments/getUniqueExperimentKey';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { useTypedAppForm } from '@gonfalon/forms/hooks';
import { MaintainerDialog } from '@gonfalon/maintainer/MaintainerDialog';
import { MemberMaintainerTag } from '@gonfalon/members/MemberMaintainerTag';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { NavigationPromptModal } from '@gonfalon/navigation-prompt/modal';
import { toExperimentDesign } from '@gonfalon/navigator/navigators';
import { checkAccess } from '@gonfalon/permissions/access';
import { useCreateExperiment } from '@gonfalon/rest-api/hooks/useCreateExperiment';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { TagAutocomplete } from '@gonfalon/tags/components/TagAutocomplete';
import { ListBoxItem } from '@gonfalon/ui/ListBox';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { Box } from '@launchpad-ui/box';
import {
  Alert,
  Button,
  Dialog,
  Form,
  Heading,
  IconButton,
  Modal,
  ModalOverlay,
  ProgressBar,
  snackbarQueue,
  toastQueue,
  Tooltip,
  TooltipTrigger,
  useOverlayTriggerState,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { FALLTHROUGH_RULE_ID } from '#components/automated-rollouts/utils';
import { useCreateExperimentationContext } from '#components/experimentation/common/contexts/CreateExperimentationContext';
import { transformFlagDataVariationsForExperimentCreation } from '#components/experimentation/common/utils/filterFlagDataVariations';
import { experimentDataSourceFromIntegrationKey } from '#components/experimentation/experiments/draft-experiment/form-sections/warehouseMetricValidation';
import { trackExperimentCreationEvent } from '#utils/analyticsUtils';

import styles from './CreateDraftExperimentModal.module.css';

type CreateDraftExperimentModalProps = {
  onCancel: () => void;
  isOpen?: boolean;
  sourceComponent?: string;
  sourceRuleId?: string;
};

export function getCreateExperimentToastTitle({
  fromAssignmentSource,
  initialFlagKey,
}: {
  fromAssignmentSource?: boolean;
  initialFlagKey?: string;
}): string {
  if (fromAssignmentSource) {
    return 'Creating experiment from assignment source...';
  }
  if (initialFlagKey) {
    return 'Creating experiment from rule...';
  }
  return 'Creating experiment...';
}

export function CreateDraftExperimentModal(props: CreateDraftExperimentModalProps) {
  const { initialFlagKey } = useCreateExperimentationContext();
  if (initialFlagKey) {
    return <CreateDraftExperimentModalWithFlag {...props} />;
  }
  return <CreateDraftExperimentModalForm {...props} />;
}

function CreateDraftExperimentModalWithFlag(props: CreateDraftExperimentModalProps) {
  const { initialFlagKey } = useCreateExperimentationContext();
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();
  const { data: flagData } = useSuspenseQuery(
    getFeatureFlagQuery({
      projectKey,
      featureFlagKey: initialFlagKey ?? '',
      query: {
        env: [environmentKey],
      },
    }),
  );
  return <CreateDraftExperimentModalForm {...props} flagData={flagData} />;
}

function CreateDraftExperimentModalForm({
  onCancel,
  isOpen = false,
  sourceComponent,
  sourceRuleId,
  flagData,
}: CreateDraftExperimentModalProps & { flagData?: FeatureFlag }) {
  const [showPrompt, setShowPrompt] = useState(false);
  const projectKey = useProjectKey();

  const context = useCreateExperimentationContext();
  const initialFlagKey = context.initialFlagKey;
  const warehouseDataSource = context.initialAssignmentIntegrationKey
    ? experimentDataSourceFromIntegrationKey(context.initialAssignmentIntegrationKey)
    : undefined;
  const assignmentSourceCreate =
    context.initialAssignmentSourceKey && warehouseDataSource
      ? { key: context.initialAssignmentSourceKey, dataSource: warehouseDataSource }
      : undefined;
  const { selectedEnvironment } = useProjectContext();
  const selectedEnvironmentKey = useSelectedEnvironmentKey();
  const environmentKey = context.initialAssignmentEnvironmentKey ?? selectedEnvironmentKey;
  const creationEnvironmentName = context.initialAssignmentEnvironmentName ?? selectedEnvironment.name;
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await form.handleSubmit();
  };

  const [closeMethod, setCloseMethod] = useState<'cancel button' | undefined>(undefined);
  const { profile } = useAccountContext();
  const hasAccess = checkAccess(profile, profile._access, 'createExperiment').isAllowed;
  const showModalState = useOverlayTriggerState({ defaultOpen: false, isOpen });
  const { mutateAsync } = useCreateExperiment();

  const routerStateSource = (location.state as { source?: CreateExperimentModalSource } | null)?.source;
  const source = resolveCreateExperimentModalSource({
    sourceComponent,
    routerStateSource,
    pathname: location.pathname,
    sourceRuleId,
  });

  const trackViewed = useCallback(
    (node: HTMLElement | null) => {
      if (node === null) {
        return;
      }
      trackCreateExperimentModalViewed({ source });
    },
    [source],
  );

  const form = useTypedAppForm<{
    experimentName: string;
    hypothesis: string;
    tags: string[];
    maintainers: Array<{ id: string; type: 'member' | 'team' }>;
  }>({
    defaultValues: {
      experimentName: '',
      hypothesis: '',
      tags: [],
      maintainers: profile._id ? [{ id: profile._id, type: 'member' as const }] : [],
    },
    validators: {
      onChange: z.object({
        experimentName: z.string().min(1, 'Experiment name is required'),
        hypothesis: z.string(),
        tags: z.array(z.string()),
        maintainers: z.array(z.object({ id: z.string(), type: z.enum(['member', 'team']) })),
      }),
      onBlur: z.object({
        experimentName: z.string().min(4, 'Experiment name is required and must be at least 4 characters'),
        hypothesis: z.string().max(1500, 'Hypothesis must be at most 1500 characters'),
        tags: z.array(z.string()),
        maintainers: z.array(z.object({ id: z.string(), type: z.enum(['member', 'team']) })),
      }),
      onSubmit: z.object({
        experimentName: z.string().min(4, 'Experiment name is required and must be at least 4 characters'),
        hypothesis: z.string().max(1500, 'Hypothesis must be at most 1500 characters'),
        tags: z.array(z.string()),
        maintainers: z.array(z.object({ id: z.string(), type: z.enum(['member', 'team']) })),
      }),
    },

    onSubmit: async ({ value }) => {
      const experimentKey = await getUniqueExperimentKey({
        experimentName: value.experimentName,
        projectKey,
        environmentKey,
      });
      try {
        toastQueue.add({
          title: getCreateExperimentToastTitle({
            fromAssignmentSource: Boolean(assignmentSourceCreate),
            initialFlagKey,
          }),
          status: 'info',
        });
        const maintainer = (value.maintainers || [])[0];
        const experiment = await mutateAsync({
          body: {
            name: value.experimentName,
            description: value.hypothesis,
            key: experimentKey,
            tags: value.tags, // don't need to flag it because we default to empty array if not provided
            methodology: 'frequentist',
            ...(maintainer?.type === 'member' ? { maintainerId: maintainer.id } : {}),
            ...(assignmentSourceCreate ? { dataSource: assignmentSourceCreate.dataSource } : {}),
            iteration: {
              flags: initialFlagKey
                ? {
                    [initialFlagKey]: {
                      flagConfigVersion: await getLatestFlagVersionId({
                        flagKey: initialFlagKey,
                        projectKey,
                        environmentKey,
                      }),
                      notInExperimentVariationId: undefined,
                      ruleId: sourceRuleId ?? FALLTHROUGH_RULE_ID,
                    },
                  }
                : {},
              treatments: flagData ? transformFlagDataVariationsForExperimentCreation(flagData, initialFlagKey) : [],
              hypothesis: value.hypothesis,
              metrics: [],
              ...(assignmentSourceCreate
                ? {
                    assignmentDataSource: {
                      key: assignmentSourceCreate.key,
                      experimentId: '',
                    },
                  }
                : {}),
              ...(experimentsDefaultDisableReshuffling() ? { canReshuffleTraffic: false } : {}),
            },
          },
          projectKey,
          environmentKey,
        });
        trackExperimentCreationEvent({
          location,
          initialFlagKey,
          experimentKey,
          flagPurpose: flagData?._purpose ?? '',
          experimentCreateEntry:
            sourceComponent === 'FlagEntity'
              ? `CreateExperimentFrom-${sourceComponent}-Sidebar`
              : (sourceComponent ?? 'CreateExperimentFromExperimentPage'),
        });
        trackCreateExperimentModalSubmitted({
          source,
          experimentId: experiment?._id,
          iterationId: experiment?.currentIteration?._id,
        });
        await navigate(toExperimentDesign({ projectKey, environmentKey, experimentKey }));
      } catch (error) {
        snackbarQueue.add({
          description: (error as Error)?.message || 'Failed to create experiment',
          status: 'error',
        });
      }
    },
  });

  const onCancelWithPrompt = (closeLocation?: 'cancel button') => {
    if (form.state.isDirty) {
      setShowPrompt(true);
      setCloseMethod(closeLocation);
    } else {
      handleModalExit(closeLocation || closeMethod);
      setCloseMethod(undefined);
    }
  };

  const handleModalExit = (closeLocation?: 'cancel button') => {
    trackCreateExperimentModalClosed({
      source,
      closeMethod: closeLocation || 'background click',
    });
    showModalState.close();
    form.reset();
    setShowPrompt(false);
    setCloseMethod(undefined);
    onCancel?.();
  };

  return (
    <ModalOverlay
      isOpen={showModalState.isOpen}
      onOpenChange={(open: boolean) => !open && !showPrompt && onCancelWithPrompt()}
    >
      <Modal>
        <Dialog>
          {({ close }) => (
            <>
              {showPrompt && (
                <NavigationPromptModal
                  onCancelNavigation={() => setShowPrompt(false)}
                  onConfirmNavigation={handleModalExit}
                  body={msg('experiments.createExperimentDialog.discardBody')}
                  confirmText={msg('experiments.createExperimentDialog.discardConfirm')}
                />
              )}
              <div ref={trackViewed} slot="header">
                <Heading slot="title">
                  {creationEnvironmentName ? (
                    <Message
                      id="experiments.createExperimentDialog.headingInEnvironment"
                      values={{ environment: creationEnvironmentName }}
                    />
                  ) : (
                    <Message id="experiments.createExperimentDialog.heading" />
                  )}
                </Heading>
                <IconButton
                  icon="cancel"
                  onPress={() => close()}
                  aria-label={msg('experiments.createExperimentDialog.closeLabel')}
                  size="small"
                  variant="minimal"
                />
                {!hasAccess && (
                  <>
                    <Alert isOpen status="error">
                      <h3>
                        <Message id="experiments.createExperimentDialog.noAccessHeading" />
                      </h3>
                      <span>
                        <Message id="experiments.createExperimentDialog.noAccessBody" />
                      </span>
                    </Alert>
                  </>
                )}
              </div>
              <Form onSubmit={handleSubmit}>
                <div slot="body" className={styles.formBody}>
                  <form.AppField name="experimentName">
                    {(field) => (
                      <field.TextInputField
                        name="experimentName"
                        label={msg('experiments.createExperimentDialog.nameLabel')}
                        description={msg('experiments.createExperimentDialog.nameDescription')}
                        placeholder={msg('experiments.createExperimentDialog.namePlaceholder')}
                        isRequired
                        autoFocus
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="hypothesis">
                    {(field) => (
                      <field.TextAreaInputField
                        name="hypothesis"
                        label={msg('experiments.createExperimentDialog.hypothesisLabel')}
                        placeholder={msg('experiments.createExperimentDialog.hypothesisPlaceholder')}
                        rows={3}
                      />
                    )}
                  </form.AppField>
                </div>
                <div slot="footer" className={styles.footer}>
                  <div className={styles.footerTags}>
                    <ErrorBoundary
                      severity="medium"
                      fallbackRender={() => (
                        <Button size="small" isDisabled>
                          <Icon name="person" size="small" slot="start-icon" />
                          <TextTruncator>
                            <Message id="experiments.createExperimentDialog.maintainersLoadError" />
                          </TextTruncator>
                        </Button>
                      )}
                    >
                      <form.AppField name="maintainers">
                        {(field) => (
                          <field.InlineTagGroup<{ id: string; type: 'member' | 'team' }>
                            placement="bottom start"
                            getItemKey={(item) => item.id}
                            getItemText={(item) => item.id}
                            emptyTrigger={
                              <TooltipTrigger>
                                <Button
                                  size="small"
                                  aria-label={msg('experiments.createExperimentDialog.addMaintainer')}
                                >
                                  <Icon name="person" size="small" />
                                </Button>
                                <Tooltip>
                                  <Message id="experiments.createExperimentDialog.addMaintainer" />
                                </Tooltip>
                              </TooltipTrigger>
                            }
                            renderAutocomplete={() => (
                              <div className={styles.popoverContainer}>
                                <Suspense
                                  fallback={
                                    <Box
                                      width="$320"
                                      height="$208"
                                      display="grid"
                                      alignItems="center"
                                      justifyContent="center"
                                    >
                                      <ProgressBar
                                        aria-label={msg('experiments.status.loading')}
                                        isIndeterminate
                                        size="small"
                                      />
                                    </Box>
                                  }
                                >
                                  <MaintainerDialog
                                    selectionMode="single"
                                    data-test-id="maintainer-dialog"
                                    closeOnSelection
                                    membersOnly
                                    maintainerId={(field.state.value || []).find((m) => m.type === 'member')?.id}
                                    onMemberSelectionChange={(key) => {
                                      const newValue: Array<{ id: string; type: 'member' | 'team' }> =
                                        key !== undefined ? [{ id: String(key), type: 'member' }] : [];
                                      field.handleChange(newValue);
                                    }}
                                  />
                                </Suspense>
                              </div>
                            )}
                          >
                            {(item) => (
                              <MemberMaintainerTag
                                className={styles.maintainerButton}
                                maintainerId={item.id}
                                id={item.id}
                                textValue={`member:${item.id}`}
                              />
                            )}
                          </field.InlineTagGroup>
                        )}
                      </form.AppField>
                    </ErrorBoundary>

                    <form.AppField name="tags">
                      {(field) => (
                        <field.InlineTagGroup
                          placement="bottom start"
                          placeholder={msg('experiments.createExperimentDialog.tagPlaceholder')}
                          tagIcon="tag"
                          emptyTrigger={
                            <TooltipTrigger>
                              <IconButton
                                icon="tag"
                                size="small"
                                aria-label={msg('experiments.createExperimentDialog.addTag')}
                              />
                              <Tooltip>
                                <Message id="experiments.createExperimentDialog.addTag" />
                              </Tooltip>
                            </TooltipTrigger>
                          }
                          multiTrigger={(count) => (
                            <TooltipTrigger>
                              <Button size="small">
                                <Icon name="tag" size="small" slot="start-icon" />
                                <TextTruncator>{count} tags</TextTruncator>
                              </Button>
                              <Tooltip>
                                <Message id="experiments.createExperimentDialog.addTag" />
                              </Tooltip>
                            </TooltipTrigger>
                          )}
                          renderAutocomplete={({ selectedKeys, onSelectionChange }) => (
                            <Dialog className={styles.tagPopoverContainer}>
                              <TagAutocomplete
                                allowCreate
                                kind="experiment"
                                selectedKeys={selectedKeys}
                                onSelectionChange={(keys) => {
                                  onSelectionChange(keys);
                                }}
                              >
                                {(item) => <ListBoxItem id={item.id}>{item.name}</ListBoxItem>}
                              </TagAutocomplete>
                            </Dialog>
                          )}
                        />
                      )}
                    </form.AppField>
                  </div>
                  <div className={styles.footerActions}>
                    <form.AppForm>
                      <Button onPress={() => onCancelWithPrompt('cancel button')} isDisabled={form.state.isSubmitting}>
                        {msg('experiments.createExperimentDialog.cancel')}
                      </Button>
                      <form.SubmitButton>
                        {msg('experiments.createExperimentDialog.createExperiment')}
                      </form.SubmitButton>
                    </form.AppForm>
                  </div>
                </div>
              </Form>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
